import { useContext, useEffect, useMemo } from "react";
import {
  ConversationContext,
  ConversationDetailsContext,
  usePhoneReply
} from "../../context";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import {
  SelectInput,
  SelectInputEventValue
} from "@interstate/components/SelectInput";
import { conversationChannelSelectorId } from "../../testIds";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import {
  getToChannelValueSelectInputOptions,
  useComponentWidth
} from "@coxauto-ui/cxm/communications/util";
import styled from "styled-components";
import classNames from "classnames";

export interface PhoneChannelValueSelectorProps {
  isDisabled: boolean;
}

const StyledChannelValueSelector = styled.div`
  overflow: hidden;
  width: 100%;

  &.large {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    &.phone-only {
      > div {
        flex: 1;
        overflow: hidden;
      }
    }
  }
`;

export function PhoneChannelValueSelector({
  isDisabled
}: PhoneChannelValueSelectorProps) {
  const [width, ref] = useComponentWidth();

  // ✅ Use phone-specific context hook
  const { selectedToChannelValue, setSelectedToChannelValue } = usePhoneReply();

  const { recentContactText } = useContext(ConversationDetailsContext);
  const { contactDetails } = useContext(ConversationContext);

  useEffect(() => {
    if (recentContactText && recentContactText !== selectedToChannelValue) {
      setSelectedToChannelValue(recentContactText);
    }
    // Note: selectedToChannelValue intentionally NOT in dependencies to avoid
    // overriding user selections when they change the dropdown value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentContactText, setSelectedToChannelValue]);

  const toChannelOptions = useMemo(
    () =>
      getToChannelValueSelectInputOptions(
        contactDetails,
        ConversationTypeEnum.PHONE
      ),
    [contactDetails]
  );

  const onChannelSelection = (
    e: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const value = e.target.value?.toString();
    if (value === undefined) return;
    if (value !== selectedToChannelValue) {
      setSelectedToChannelValue(value);
    }
  };

  return (
    <StyledChannelValueSelector
      className={classNames({
        "phone-only": true,
        large: width > 600
      })}
      ref={ref}
    >
      <SelectInput
        displayLabel
        data-testid={conversationChannelSelectorId}
        disabled={isDisabled}
        displayDeselectOption={false}
        id="conversation-channel-pane-dropdown"
        label="Choose Phone Number"
        name="ConversationChannelValueDropdown"
        options={toChannelOptions}
        value={selectedToChannelValue}
        onChange={onChannelSelection}
      />
    </StyledChannelValueSelector>
  );
}

export default PhoneChannelValueSelector;
